import { useState } from 'react';
import { Alert, Form, Input, message, Modal, Typography } from 'antd';
import type { UserResponse } from '../../../api/types';
import { useT } from '../../../locales';
import { useCreateKeycloakUser, type CreateKeycloakUserInput } from '../api/useUsers';
import { TemporaryPasswordModal } from './TemporaryPasswordModal';

export interface CreateKeycloakUserModalProps {
  open: boolean;
  user: UserResponse;
  onClose: () => void;
}

interface CreateKeycloakUserFormValues {
  email: string;
  username: string;
  firstName: string;
  lastName: string;
}

const CREATE_KEYCLOAK_USER_FIELD_LIMITS = {
  email: 255,
  username: 255,
  firstName: 100,
  lastName: 100,
} as const;

export function CreateKeycloakUserModal({ open, user, onClose }: CreateKeycloakUserModalProps) {
  const t = useT();
  const [form] = Form.useForm<CreateKeycloakUserFormValues>();
  const createKeycloakUser = useCreateKeycloakUser();
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const initialValues: CreateKeycloakUserFormValues = {
    email: user.email,
    username: user.username,
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
  };

  const handleCancel = () => {
    if (createKeycloakUser.isPending) return;
    form.resetFields();
    createKeycloakUser.reset();
    onClose();
  };

  const handleFinish = async (values: CreateKeycloakUserFormValues) => {
    try {
      const input: CreateKeycloakUserInput = {
        id: user.id,
        body: values,
      };
      const result = await createKeycloakUser.mutateAsync(input);
      form.resetFields();
      createKeycloakUser.reset();
      onClose();
      setTempPassword(result.tempPassword);
      message.success(t('pages.users.createKeycloakSuccess'));
    } catch {
      message.error(t('pages.users.createKeycloakError'));
    }
  };

  const handleAcknowledge = () => {
    setTempPassword(null);
    createKeycloakUser.reset();
  };

  return (
    <>
      <Modal
        open={open}
        title={t('pages.users.createKeycloakTitle')}
        onOk={() => form.submit()}
        onCancel={handleCancel}
        confirmLoading={createKeycloakUser.isPending}
        okText={t('pages.users.createKeycloakConfirm')}
        cancelText={t('common.cancel')}
        destroyOnHidden
        centered
        width={520}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
        afterOpenChange={(nextOpen) => {
          if (nextOpen) {
            form.setFieldsValue(initialValues);
          } else {
            form.resetFields();
          }
        }}
      >
        <Typography.Paragraph type="secondary">
          {t('pages.users.createKeycloakDescription')}
        </Typography.Paragraph>
        {createKeycloakUser.error && (
          <Alert
            type="error"
            showIcon
            title={t('pages.users.createKeycloakError')}
            style={{ marginBottom: 16 }}
          />
        )}
        <Form<CreateKeycloakUserFormValues>
          form={form}
          layout="vertical"
          preserve={false}
          clearOnDestroy
          onFinish={handleFinish}
          initialValues={initialValues}
        >
          <Form.Item
            name="email"
            label={t('pages.users.email')}
            rules={[
              { required: true, message: t('pages.users.createKeycloakEmailRequired') },
              { type: 'email', message: t('pages.users.invalidEmail') },
              {
                max: CREATE_KEYCLOAK_USER_FIELD_LIMITS.email,
                message: t('pages.users.createKeycloakEmailTooLong'),
              },
            ]}
          >
            <Input autoComplete="email" maxLength={CREATE_KEYCLOAK_USER_FIELD_LIMITS.email} />
          </Form.Item>
          <Form.Item
            name="username"
            label={t('pages.users.username')}
            rules={[
              { required: true, message: t('pages.users.createKeycloakUsernameRequired') },
              {
                max: CREATE_KEYCLOAK_USER_FIELD_LIMITS.username,
                message: t('pages.users.createKeycloakUsernameTooLong'),
              },
            ]}
          >
            <Input autoComplete="username" maxLength={CREATE_KEYCLOAK_USER_FIELD_LIMITS.username} />
          </Form.Item>
          <Form.Item
            name="firstName"
            label={t('pages.users.firstName')}
            rules={[
              { required: true, message: t('pages.users.firstNameRequired') },
              {
                max: CREATE_KEYCLOAK_USER_FIELD_LIMITS.firstName,
                message: t('pages.users.firstNameTooLong'),
              },
            ]}
          >
            <Input
              autoComplete="given-name"
              maxLength={CREATE_KEYCLOAK_USER_FIELD_LIMITS.firstName}
            />
          </Form.Item>
          <Form.Item
            name="lastName"
            label={t('pages.users.lastName')}
            rules={[
              { required: true, message: t('pages.users.lastNameRequired') },
              {
                max: CREATE_KEYCLOAK_USER_FIELD_LIMITS.lastName,
                message: t('pages.users.lastNameTooLong'),
              },
            ]}
          >
            <Input
              autoComplete="family-name"
              maxLength={CREATE_KEYCLOAK_USER_FIELD_LIMITS.lastName}
            />
          </Form.Item>
        </Form>
      </Modal>
      <TemporaryPasswordModal
        open={tempPassword !== null}
        tempPassword={tempPassword}
        onAcknowledge={handleAcknowledge}
      />
    </>
  );
}
